import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import SummaryApi from '../common'
import { loadWishlist } from '../utils/wishlistStorage'

// Wishlist state — array of product-id strings.
//
// Persistence: the store subscribes to wishlist state changes in `store.js` and writes
// the items array to localStorage there. Reducers/cases in this file are pure state
// transitions only; they used to each call `saveWishlist(state.items)` inline, which was
// easy to forget on a new reducer and made the slice harder to reason about. The single
// persist path is now in store.js.

// Fetch wishlist from backend if authed
export const fetchWishlist = createAsyncThunk('wishlist/fetch', async (_, { rejectWithValue }) => {
    try {
        const res = await fetch(SummaryApi.getWishlist.url, {
            method: SummaryApi.getWishlist.method,
            credentials: 'include',
        })
        if (!res.ok) return rejectWithValue(await res.json())
        const json = await res.json()
        return Array.isArray(json?.data) ? json.data.map(String) : []
    } catch (err) {
        return rejectWithValue({ message: err.message || 'Failed to fetch wishlist' })
    }
})

export const putWishlist = createAsyncThunk('wishlist/put', async (items, { rejectWithValue }) => {
    try {
        const res = await fetch(SummaryApi.putWishlist.url, {
            method: SummaryApi.putWishlist.method,
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ items: Array.isArray(items) ? items.map(String) : [] }),
        })
        if (!res.ok) return rejectWithValue(await res.json())
        const json = await res.json()
        return Array.isArray(json?.data) ? json.data.map(String) : []
    } catch (err) {
        return rejectWithValue({ message: err.message || 'Failed to save wishlist' })
    }
})

export const toggleWishlistRemote = createAsyncThunk('wishlist/toggleRemote', async (productId, { rejectWithValue }) => {
    try {
        const res = await fetch(SummaryApi.toggleWishlist.url, {
            method: SummaryApi.toggleWishlist.method,
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ productId }),
        })
        if (!res.ok) return rejectWithValue(await res.json())
        const json = await res.json()
        return Array.isArray(json?.data) ? json.data.map(String) : []
    } catch (err) {
        return rejectWithValue({ message: err.message || 'Failed to toggle wishlist' })
    }
})

const initialState = {
    items: loadWishlist(), // hydrate from localStorage on init
    status: 'idle',
    error: null,
}

const wishlistSlice = createSlice({
    name: 'wishlist',
    initialState,
    reducers: {
        toggleWishlist(state, action) {
            const id = String(action.payload)
            const idx = state.items.indexOf(id)
            if (idx >= 0) state.items.splice(idx, 1)
            else state.items.push(id)
        },
        addManyToWishlist(state, action) {
            const set = new Set(state.items)
            ;(action.payload || []).map(String).forEach((id) => set.add(id))
            state.items = Array.from(set)
        },
        clearWishlist(state) {
            state.items = []
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWishlist.pending, (state) => { state.status = 'loading'; state.error = null })
            .addCase(fetchWishlist.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.items = action.payload
            })
            .addCase(fetchWishlist.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.payload?.message || 'Failed to load wishlist'
            })
            .addCase(putWishlist.fulfilled, (state, action) => { state.items = action.payload })
            .addCase(toggleWishlistRemote.fulfilled, (state, action) => { state.items = action.payload })
    },
})

export const selectWishlistItems = (state) => state?.wishlist?.items || []
export const { toggleWishlist, addManyToWishlist, clearWishlist } = wishlistSlice.actions
export default wishlistSlice.reducer
